import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AwsCredentials, MsalCredentials } from 'app/models/domain/auth'
import { TuhiUserData } from 'app/models/domain/user'
import { store } from '..'

// The credential and user fields hold null when signed out: initialState and
// clearAuthState both set null, so the type says so.
export interface AuthSliceState {
    readonly isLoading: boolean
    readonly isAuthenticated?: boolean
    readonly isAuthorized?: boolean
    readonly isAmplifyConfigured: boolean
    readonly msalCredentials?: MsalCredentials | null
    readonly awsCredentials?: AwsCredentials | null
    readonly currentUser?: TuhiUserData | null
}

const initialState: AuthSliceState = {
    isLoading: false,
    isAuthenticated: false,
    isAuthorized: false,
    isAmplifyConfigured: false,
    msalCredentials: null,
    awsCredentials: null,
    currentUser: null,
}

export const authSlice = createSlice({
    name: 'auth',
    initialState: initialState,
    reducers: {
        setMsalCredentials: (
            state,
            // Auth responses carry these as optional; the state allows it.
            action: PayloadAction<MsalCredentials | undefined>
        ) => {
            state.msalCredentials = action.payload
        },
        setAwsCredentials: (
            state,
            action: PayloadAction<AwsCredentials | undefined>
        ) => {
            state.awsCredentials = action.payload
        },
        setTuhiUser: (state, action: PayloadAction<TuhiUserData>) => {
            // Store plain object directly (already serializable)
            state.currentUser = action.payload
            state.isAuthenticated = true
            state.isAuthorized =
                !!action.payload.id &&
                String(action.payload.confidenceLevel) === '3'
        },
        setAmplifyConfigured: (state, action: PayloadAction<boolean>) => {
            state.isAmplifyConfigured = action.payload
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload
        },
        clearAuthState: (state) => {
            state.isLoading = false
            state.isAuthenticated = false
            state.isAuthorized = false
            state.isAmplifyConfigured = false
            state.awsCredentials = null
            state.msalCredentials = null
            state.currentUser = null
        },
    },
})

export const {
    setMsalCredentials,
    setAwsCredentials,
    setTuhiUser,
    setAmplifyConfigured,
    setIsLoading,
    clearAuthState,
} = authSlice.actions

export const authReducer = authSlice.reducer
export const getTuhiUserId = () => store.getState().auth.currentUser?.id
export const getAwsCredentials = () => store.getState().auth.awsCredentials
